/**
 * Renders a roster member's declared fields — ONE handler per TYPE, never per POV.
 *
 * That rule is the whole mechanism. A roster declares `{key, label, type}` and
 * this file knows how to draw each of the five types; it does not know, and
 * must never learn, that a class year belongs to a collegiate programme or a
 * role to an esports org. `if (pov === …)` here would make the sixth POV cost
 * a renderer change, which is the per-host hand-rolling the shared resolver
 * exists to end.
 *
 * ── WHY THIS EXISTS SEPARATELY FROM THE CARD ──
 *
 * The resolver has been returning `fields` since it was built, and no host
 * rendered them — the schema reached the payload and stopped. PAGE's card and
 * HUB's rail have entirely different markup and neither should own the
 * type-handling, so it lives here and both mount it.
 */

import React from "react";
import {RosterFieldType} from "@devour/client";
import {type ResolvedRosterField} from "@/surfaces/rosterFields";
import "./RosterFieldValues.scss";

interface RosterFieldValuesProps {
    fields: readonly ResolvedRosterField[];
    /** Extra class for the host to place the row. Never for styling a field. */
    className?: string;
}

/** One field, drawn by its declared type. */
function RosterFieldValue({field}: { field: ResolvedRosterField }): React.ReactElement | null {
    const {spec, value} = field;

    switch (spec.type) {
        case RosterFieldType.CHIP:
            return (
                <span className="rf__chip" title={spec.label}>{String(value)}</span>
            );

        case RosterFieldType.PERCENTAGE: {
            /*
             * A percentage is stored as the number an operator typed. Rendering
             * `0.42` as "0.42%" would be wrong and `42` as "4200%" equally so,
             * and nothing in the schema says which convention a roster used —
             * so the value is shown as given, with the unit appended. The
             * schema's `label` is what says what it measures.
             */
            const n = typeof value === "number" ? value : Number(value);
            if (!Number.isFinite(n)) return null;
            return (
                <span className="rf__pct" title={spec.label}>
                    {n}
                    <i aria-hidden="true">%</i>
                </span>
            );
        }

        case RosterFieldType.IMAGE:
            /*
             * `alt` is the field's LABEL, not its value. The value is a URL,
             * and a country flag's alt text should read "Country", not
             * "https://…/gb.svg".
             */
            return <img className="rf__img" src={String(value)} alt={spec.label} loading="lazy" />;

        case RosterFieldType.FLAG:
            /*
             * A false flag renders NOTHING. Its whole job is that its presence
             * is the message — "rights cleared" absent means not cleared, and
             * drawing a struck-through badge for every member who lacks it
             * turns a quiet fact into an accusation on the card.
             *
             * The label carries the meaning, so it is the accessible name
             * rather than a `title` a screen reader may skip.
             */
            if (!value) return null;
            return <span className="rf__flag" aria-label={spec.label}>{spec.label}</span>;

        case RosterFieldType.TEXT:
        default:
            return <span className="rf__text" title={spec.label}>{String(value)}</span>;
    }
}

export function RosterFieldValues({fields, className}: RosterFieldValuesProps): React.ReactElement | null {
    /* Nothing declared, or nothing resolved — render no row at all, not an empty one. */
    if (!fields.length) return null;

    return (
        <span className={className ? `rf ${className}` : "rf"}>
            {fields.map(f => <RosterFieldValue key={f.spec.key} field={f} />)}
        </span>
    );
}

export default RosterFieldValues;
